import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '../common';

export const SceneModal = ({
  isOpen,
  onClose,
  scene = null,
  characters = [],
  onSave,
  onDelete,
  isSaving = false,
  canDelete = false,
}) => {
  const isEditing = !!scene;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    characterIds: [],
  });

  const [confirmDelete, setConfirmDelete] = useState(false);

  // Reset form when modal opens/closes or scene changes
  useEffect(() => {
    if (isOpen) {
      if (scene) {
        setFormData({
          title: scene.title || '',
          description: scene.description || '',
          characterIds: (scene.characterIds || []).map(c => c.id || c._id || c),
        });
      } else {
        setFormData({
          title: '',
          description: '',
          characterIds: [],
        });
      }
      setConfirmDelete(false);
    }
  }, [isOpen, scene]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleCharacterToggle = (characterId) => {
    setFormData((prev) => {
      const isSelected = prev.characterIds.includes(characterId);
      return {
        ...prev,
        characterIds: isSelected
          ? prev.characterIds.filter(id => id !== characterId)
          : [...prev.characterIds, characterId],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave(formData);
  };

  const handleDelete = async () => {
    if (confirmDelete) {
      await onDelete(scene.id || scene._id);
      onClose();
    } else {
      setConfirmDelete(true);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit Scene ${scene?.number || ''}` : 'Create Scene'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title"
          value={formData.title}
          onChange={handleChange('title')}
          placeholder="Scene title (optional)"
          maxLength={200}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={handleChange('description')}
            placeholder="Describe the setting, mood, or situation for this scene..."
            rows={4}
            maxLength={2000}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {/* Character Selection */}
        {characters.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Characters in Scene
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {characters.map((character) => {
                const charId = character.id || character._id;
                const isSelected = formData.characterIds.includes(charId);
                return (
                  <label
                    key={charId}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleCharacterToggle(charId)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {character.name}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formData.characterIds.length} character{formData.characterIds.length !== 1 ? 's' : ''} selected
            </p>
          </div>
        )}

        <div className="flex justify-between items-center pt-4 border-t">
          <div>
            {isEditing && onDelete && canDelete && (
              <Button
                type="button"
                variant={confirmDelete ? 'danger' : 'secondary'}
                onClick={handleDelete}
                disabled={isSaving}
              >
                {confirmDelete ? 'Confirm Delete' : 'Delete Scene'}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSaving}
            >
              {isEditing ? 'Save Changes' : 'Create Scene'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
